<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Get in touch</p>
        <h1 class="info-h1">We'd love to<br><em>hear from you</em></h1>
        <p class="info-lead">
          Have a question, a suggestion, or need help with your account?
          Fill in the form below and we'll get back to you within one business day.
        </p>
      </div>
    </div>

    <div class="info-body">
      <div class="contact-layout">

        <!-- Form -->
        <div class="contact-form-col">
          <!-- Success state -->
          <div v-if="submitted" class="contact-success">
            <div class="contact-success__icon">✅</div>
            <h2 class="contact-success__title">Message sent!</h2>
            <p class="contact-success__desc">
              Thanks for reaching out. We'll reply to <strong>{{ form.email }}</strong> within one business day.
            </p>
            <RouterLink
              :to="isAuthenticated ? '/parent/dashboard' : '/'"
              class="contact-reset-btn"
            >{{ isAuthenticated ? 'Return to dashboard' : 'Return to homepage' }}</RouterLink>
          </div>

          <!-- Form -->
          <form v-else class="contact-form" @submit.prevent="handleSubmit" novalidate>
            <div class="form-row">
              <div class="form-field">
                <label class="form-label" for="cf-name">Full name *</label>
                <input
                  id="cf-name"
                  v-model="form.name"
                  type="text"
                  class="form-input"
                  :class="{ 'form-input--error': errors.name }"
                  placeholder="e.g. Sarah Johnson"
                  autocomplete="name"
                />
                <span v-if="errors.name" class="form-error">{{ errors.name }}</span>
              </div>
              <div class="form-field">
                <label class="form-label" for="cf-email">Email address *</label>
                <input
                  id="cf-email"
                  v-model="form.email"
                  type="email"
                  class="form-input"
                  :class="{ 'form-input--error': errors.email }"
                  placeholder="you@example.com"
                  autocomplete="email"
                  @blur="onEmailBlur"
                />
                <span v-if="errors.email" class="form-error">{{ errors.email }}</span>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="cf-subject">Subject *</label>
              <select
                id="cf-subject"
                v-model="form.subject"
                class="form-input form-select"
                :class="{ 'form-input--error': errors.subject }"
              >
                <option value="" disabled>Select a topic…</option>
                <option value="Account & billing">Account &amp; billing</option>
                <option value="Technical issue">Technical issue</option>
                <option value="Curriculum question">Curriculum question</option>
                <option value="Feature request">Feature request</option>
                <option value="School / group enquiry">School / group enquiry</option>
                <option value="Other">Other</option>
              </select>
              <span v-if="errors.subject" class="form-error">{{ errors.subject }}</span>
            </div>

            <div class="form-field">
              <div class="form-label-row">
                <label class="form-label" for="cf-message">Message *</label>
                <span class="char-count" :class="{ 'char-count--met': form.message.trim().length >= 10 }">
                  {{ form.message.trim().length }}<span v-if="form.message.trim().length < 10"> / 10 min</span>
                </span>
              </div>
              <textarea
                id="cf-message"
                v-model="form.message"
                class="form-input form-textarea"
                :class="{ 'form-input--error': errors.message }"
                placeholder="Tell us how we can help…"
                rows="6"
              />
              <span v-if="errors.message" class="form-error">{{ errors.message }}</span>
            </div>

            <!-- Math captcha -->
            <div class="form-field">
              <label class="form-label" for="cf-captcha">
                Security check: what is {{ captchaA }} + {{ captchaB }}? *
              </label>
              <input
                id="cf-captcha"
                v-model="captchaAnswer"
                type="number"
                class="form-input captcha-input"
                :class="{ 'form-input--error': captchaError }"
                placeholder="Your answer"
                autocomplete="off"
              />
              <span v-if="captchaError" class="form-error">{{ captchaError }}</span>
            </div>

            <p v-if="submitError" class="form-submit-error">{{ submitError }}</p>

            <button type="submit" class="contact-submit-btn" :disabled="!isFormReady || submitting">
              <span v-if="submitting" class="contact-submit-btn__spinner">⏳</span>
              {{ submitting ? 'Sending…' : 'Send message' }}
            </button>
          </form>
        </div>

        <!-- Side info -->
        <div class="contact-info-col">
          <div class="contact-card">
            <div class="contact-card__icon">📧</div>
            <h3 class="contact-card__title">Email support</h3>
            <p class="contact-card__desc">For non-urgent queries, email us directly.</p>
            <a href="mailto:contact@mathselevenplus.co.uk" class="contact-card__link">contact@mathselevenplus.co.uk</a>
          </div>
          <div class="contact-card">
            <div class="contact-card__icon">⏱️</div>
            <h3 class="contact-card__title">Response time</h3>
            <p class="contact-card__desc">We aim to respond to all enquiries within one business day (Monday–Friday, 9am–5pm GMT).</p>
          </div>
          <div class="contact-card">
            <div class="contact-card__icon">📚</div>
            <h3 class="contact-card__title">Check the FAQ first</h3>
            <p class="contact-card__desc">Many common questions are answered in our help centre.</p>
            <RouterLink to="/info/faq" class="contact-card__link">Browse FAQs →</RouterLink>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import { submitContactForm } from '@/services/contactService';
import { usePageMeta } from '@/composables/usePageMeta';
import { useAnalytics } from '@/composables/useAnalytics';
import { useAuth } from '@/composables/useAuth';
usePageMeta({ title: 'Contact Us — Maths Eleven Plus', description: 'Get in touch with the Maths Eleven Plus support team. We reply within one business day.' });
const { track } = useAnalytics();
const { isAuthenticated } = useAuth();

const form = reactive({ name: '', email: '', subject: '', message: '' });
const errors = reactive({ name: '', email: '', subject: '', message: '' });
const submitting = ref(false);
const submitted = ref(false);
const submitError = ref('');

// ─── Math captcha ─────────────────────────────────────────────────────────────
const captchaA = ref(0);
const captchaB = ref(0);
const captchaAnswer = ref('');
const captchaError = ref('');

function newChallenge() {
  captchaA.value = Math.floor(Math.random() * 9) + 1;
  captchaB.value = Math.floor(Math.random() * 9) + 1;
  captchaAnswer.value = '';
  captchaError.value = '';
}

onMounted(newChallenge);
// ─────────────────────────────────────────────────────────────────────────────

const _emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Enables the submit button only when every field is filled and email is valid.
const isFormReady = computed(() =>
  form.name.trim().length > 0 &&
  _emailRe.test(form.email.trim()) &&
  form.subject.length > 0 &&
  form.message.trim().length >= 10 &&
  captchaAnswer.value !== ''
);

// Show email format error as soon as the user leaves the field (if they typed something).
function onEmailBlur() {
  if (!form.email.trim()) return;
  errors.email = _emailRe.test(form.email.trim()) ? '' : 'Please enter a valid email address.';
}

function validate() {
  errors.name = form.name.trim() ? '' : 'Please enter your name.';
  errors.subject = form.subject ? '' : 'Please select a subject.';
  errors.message = form.message.trim().length >= 10 ? '' : 'Please enter a message (at least 10 characters).';
  errors.email = _emailRe.test(form.email.trim()) ? '' : 'Please enter a valid email address.';
  return !(errors.name || errors.email || errors.subject || errors.message);
}

async function handleSubmit() {
  submitError.value = '';
  captchaError.value = '';
  if (!validate()) return;
  if (parseInt(captchaAnswer.value, 10) !== captchaA.value + captchaB.value) {
    newChallenge();
    captchaError.value = 'Incorrect answer — please try again.';
    return;
  }
  submitting.value = true;
  try {
    await submitContactForm({ name: form.name.trim(), email: form.email.trim(), subject: form.subject, message: form.message.trim() });
    submitted.value = true;
    track('contact_submitted', { subject: form.subject });
  } catch (e) {
    submitError.value = 'Sorry, something went wrong. Please try again or email us directly at contact@mathselevenplus.co.uk.';
  } finally {
    submitting.value = false;
  }
}

</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }
.info-hero { background: #0D1628; padding: 5rem 2rem 4rem; }
.info-hero__inner { max-width: 52rem; margin: 0 auto; }
.info-eyebrow { font-size: 0.8rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #B8902A; margin-bottom: 1rem; }
.info-h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(2.25rem, 5vw, 3.25rem); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 1.25rem; }
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead { font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 44rem; line-height: 1.7; }

.info-body { max-width: 68rem; margin: 0 auto; padding: 3rem 2rem 4rem; }

.contact-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}
@media (min-width: 900px) {
  .contact-layout { grid-template-columns: 1.7fr 1fr; }
}

/* Form */
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
@media (min-width: 640px) { .form-row { grid-template-columns: 1fr 1fr; } }
.form-field { display: flex; flex-direction: column; gap: 0.375rem; }
.form-label { font-size: 0.825rem; font-weight: 600; color: #374151; }
.form-label-row { display: flex; align-items: baseline; justify-content: space-between; }
.char-count { font-size: 0.775rem; color: #ef4444; font-weight: 600; }
.char-count--met { color: #16a34a; }
.form-input {
  padding: 0.625rem 0.875rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-family: 'Jost', sans-serif;
  color: #111827;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}
.form-input:focus { border-color: #B8902A; box-shadow: 0 0 0 3px rgba(184,144,42,0.15); }
.form-input--error { border-color: #ef4444; }
.form-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.75rem center; padding-right: 2.5rem; }
.form-textarea { resize: vertical; min-height: 120px; }
.form-error { font-size: 0.8rem; color: #ef4444; }
.form-submit-error { font-size: 0.875rem; color: #ef4444; background: #fef2f2; border: 1px solid #fecaca; border-radius: 0.5rem; padding: 0.75rem 1rem; }
.captcha-input { max-width: 120px; }

.contact-submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #B8902A, #d4a843);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  font-family: 'Jost', sans-serif;
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: opacity 0.15s;
  align-self: flex-start;
}
.contact-submit-btn:hover:not(:disabled) { opacity: 0.88; }
.contact-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Success */
.contact-success { text-align: center; padding: 3rem 1rem; }
.contact-success__icon { font-size: 3rem; margin-bottom: 1rem; }
.contact-success__title { font-family: 'Cormorant Garamond', serif; font-size: 2rem; font-weight: 700; color: #0D1628; margin-bottom: 0.75rem; }
.contact-success__desc { font-size: 0.975rem; color: #4b5563; line-height: 1.7; margin-bottom: 1.5rem; }
.contact-reset-btn { display: inline-block; text-decoration: none; background: none; border: 1.5px solid #B8902A; color: #B8902A; font-weight: 600; font-size: 0.875rem; font-family: 'Jost', sans-serif; padding: 0.5rem 1.25rem; border-radius: 0.375rem; cursor: pointer; transition: all 0.15s; }
.contact-reset-btn:hover { background: #B8902A; color: #fff; }

/* Side info cards */
.contact-info-col { display: flex; flex-direction: column; gap: 1rem; }
.contact-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
}
.contact-card__icon { font-size: 1.5rem; margin-bottom: 0.5rem; }
.contact-card__title { font-size: 0.95rem; font-weight: 700; color: #0D1628; margin-bottom: 0.375rem; }
.contact-card__desc { font-size: 0.875rem; color: #6b7280; line-height: 1.6; margin-bottom: 0.5rem; }
.contact-card__link { font-size: 0.875rem; color: #B8902A; font-weight: 600; text-decoration: none; }
.contact-card__link:hover { text-decoration: underline; }
</style>
